import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { logger } from 'modules/logger';
import { getWorkspaceManagerVersion, isWorkspaceManagerAvailable } from 'modules/workspace-manager';
import {
  addExtensionListener,
  removeExtensionListener,
  dispatchExtensionEvent,
  focusEvent,
} from 'domains/extension/extensionEventCreators';
import { RESPONSE_EVENTS as EXTENSION_RESPONSE_EVENTS } from 'domains/extension/constants';
import { getWindowName } from 'hooks/useOpenTabs';
import { ExtWindowName } from 'hooks/useExtensionState';
import { getPageType, PageTypes } from 'utils/pageTypes';

export const EXT_VERSION_REQUIRED = 4;

export type Transition = {
  /** Target extension window name */
  target: ExtWindowName | PageTypes;
  /** New path */
  path: string;
  /** Focus the window after transition */
  focus?: boolean;
};

export function useRouteTransitions(): void {
  const navigate = useNavigate();
  const [isExtensionReady, setIsExtensionReady] = useState(() => {
    // Check initial availability and version
    return isWorkspaceManagerAvailable() && getWorkspaceManagerVersion() >= EXT_VERSION_REQUIRED;
  });

  // Handle dynamic extension loading
  useEffect(() => {
    const checkExtensionStatus = () => {
      const isAvailable = isWorkspaceManagerAvailable();
      const version = getWorkspaceManagerVersion();
      const isReady = isAvailable && version >= EXT_VERSION_REQUIRED;

      if (isReady && !isExtensionReady) {
        logger.info('[useRouteTransitions] Extension became available with adequate version');
      } else if (isAvailable && version < EXT_VERSION_REQUIRED) {
        logger.warn(
          `[useRouteTransitions] Extension is available but version ${version} is less than required ${EXT_VERSION_REQUIRED}`
        );
      }

      setIsExtensionReady(isReady);
    };

    // If extension not ready, listen for injection event
    if (!isExtensionReady) {
      const handleExtensionInjected = () => {
        logger.info('[useRouteTransitions] Extension injection detected, checking status');
        checkExtensionStatus();
      };

      addExtensionListener(EXTENSION_RESPONSE_EVENTS.EXTENSION_INJECTED, handleExtensionInjected);

      return () => {
        removeExtensionListener(
          EXTENSION_RESPONSE_EVENTS.EXTENSION_INJECTED,
          handleExtensionInjected
        );
      };
    }
  }, [isExtensionReady]);

  // Setup route transition handlers when extension is ready
  useEffect(() => {
    if (!isExtensionReady) {
      return;
    }

    const transitionRouteHandler = (event: CustomEvent<Transition>) => {
      const { detail } = event;
      logger.info('[useRouteTransitions] Transition Route', detail);
      const currentWindow = getWindowName(window.location.href);
      const currentPage = getPageType(window.location.href);
      const loggerInfo = {
        ...detail,
        currentUrl: window.location.href,
      } as const;
      // TODO: Ideally this would validate against a list of defined paths
      if (detail.path == null || detail.path === '') {
        logger.error('[useRouteTransitions] Transition Route: Invalid path', loggerInfo);
        return;
      }

      if (currentWindow === detail.target || currentPage === detail.target) {
        // Compare target path with current href to avoid redundant navigations
        let isSameRoute = false;
        try {
          const currentUrl = new URL(window.location.href);
          const targetUrl = new URL(detail.path, window.location.origin);
          const currentPath = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
          const targetPath = `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
          isSameRoute = currentPath === targetPath;
        } catch {
          // If URL parsing fails, fall back to navigating
          isSameRoute = false;
        }

        if (!isSameRoute) {
          navigate(detail.path);
        }

        if (detail.focus) {
          dispatchExtensionEvent(focusEvent());
        }
      } else {
        // Message should only be sent to the appropriate tab, so if target
        // doesn't match it is an error
        logger.error(
          '[useRouteTransitions] Transition Route: Current window does not match target window',
          loggerInfo
        );
      }
    };

    addExtensionListener(
      EXTENSION_RESPONSE_EVENTS.TRANSITION_ROUTE_RESPONSE,
      // @ts-expect-error [EN-10263] - TS2345 - Argument of type '(event: CustomEvent<Transition>) => void' is not assignable to parameter of type 'EventListenerOrEventListenerObject'.
      transitionRouteHandler
    );

    return () => {
      removeExtensionListener(
        EXTENSION_RESPONSE_EVENTS.TRANSITION_ROUTE_RESPONSE,
        // @ts-expect-error [EN-10263] - TS2345 - Argument of type '(event: CustomEvent<Transition>) => void' is not assignable to parameter of type 'EventListenerOrEventListenerObject'.
        transitionRouteHandler
      );
    };
  }, [isExtensionReady, navigate]);
}
